import type { StepKind } from '@sb/routine-runner';

import { getPermittedChildStepKindsShared } from '../../step-rules/moveArmToV2';
import { type ValidationRule, type ValidationContext } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';

class MoveArmToV2Rule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.MoveArmToV2', 'MoveArmToV2');
  }

  protected validate({
    children,
    ctx,
  }: {
    children: Array<{ stepKind: StepKind | string; name?: string }>;
    ctx: ValidationContext;
  }): void {
    const permittedSteps = this.getPermittedChildSteps(ctx);
    this.checkChildrenKinds(children, permittedSteps);
  }

  /**
   * Get the permitted child steps.
   * @param ctx - The context.
   * @returns The permitted child steps.
   */
  private getPermittedChildSteps(ctx: ValidationContext): readonly StepKind[] {
    return getPermittedChildStepKindsShared(ctx.featureFlags ?? {});
  }

  /**
   * Check if the children kinds are valid.
   * @param children - The children.
   * @param permittedSteps - The permitted steps.
   * @throws {ActionRequiredError}
   */
  private checkChildrenKinds(
    children: Array<{ stepKind: StepKind | string; name?: string }>,
    permittedSteps: readonly StepKind[],
  ): void {
    const allOk = children.every((c) =>
      permittedSteps.includes(c.stepKind as StepKind),
    );

    if (allOk) return;

    const badStep = children.find(
      (c) => !permittedSteps.includes(c.stepKind as StepKind),
    );

    if (!badStep) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Move Arm steps must contain only valid child steps.',
      });
    }

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Move Arm steps may not contain ${badStep.stepKind} steps.`,
    });
  }
}

export const MoveArmToV2Validation: ValidationRule =
  new MoveArmToV2Rule().toValidationRule();
